import { apiClient } from "./api";

export interface CopilotThreadRecord {
  id: string;
  org_id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface CopilotMessageRecord {
  id: string;
  thread_id: string;
  role: "user" | "assistant" | "system" | "tool";
  content_type: "text" | "markdown" | "json";
  content_payload: unknown;
  attachments: unknown;
  token_usage?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    credits_consumed: number;
  };
  created_at: string;
}

export interface CopilotWorkflowStateRecord {
  thread_id: string;
  workflow_memory: unknown;
  current_node: string;
  context_snapshot: unknown;
  updated_at: string;
}

export interface CopilotThreadWithMessagesResponse {
  thread: CopilotThreadRecord;
  messages: CopilotMessageRecord[];
  state?: CopilotWorkflowStateRecord;
}

export interface CopilotCreateMessagePayload {
  role: "user" | "assistant" | "system" | "tool";
  content_type: "text" | "markdown" | "json";
  content_payload: unknown;
  attachments?: unknown;
}

export interface CopilotCreateThreadPayload {
  title?: string;
  first_message?: CopilotCreateMessagePayload;
}

export async function listCopilotThreads(
  limit = 50,
  offset = 0,
): Promise<CopilotThreadRecord[]> {
  const { data } = await apiClient.get<{ threads: CopilotThreadRecord[] }>(
    "/copilot/threads",
    { params: { limit, offset } },
  );
  return data.threads ?? [];
}

export async function createCopilotThread(
  payload: CopilotCreateThreadPayload,
): Promise<{
  thread: CopilotThreadRecord;
  first_message?: CopilotMessageRecord | null;
}> {
  const { data } = await apiClient.post<{
    thread: CopilotThreadRecord;
    first_message?: CopilotMessageRecord | null;
  }>("/copilot/threads", payload);
  return data;
}

export async function getCopilotThreadMessages(
  threadId: string,
  limit = 200,
  offset = 0,
): Promise<CopilotThreadWithMessagesResponse> {
  const { data } = await apiClient.get<CopilotThreadWithMessagesResponse>(
    `/copilot/threads/${threadId}/messages`,
    { params: { limit, offset } },
  );
  return data;
}

export async function createCopilotMessage(
  threadId: string,
  payload: CopilotCreateMessagePayload,
): Promise<CopilotMessageRecord> {
  const { data } = await apiClient.post<CopilotMessageRecord>(
    `/copilot/threads/${threadId}/messages`,
    payload,
  );
  return data;
}

export async function truncateCopilotThreadMessages(
  threadId: string,
  startIndex: number,
): Promise<{ deleted_count: number }> {
  const { data } = await apiClient.post<{ deleted_count: number }>(
    `/copilot/threads/${threadId}/messages/truncate`,
    { start_index: startIndex },
  );
  return data;
}

export async function deleteCopilotThread(threadId: string): Promise<void> {
  await apiClient.delete(`/copilot/threads/${threadId}`);
}

export async function updateCopilotThreadTitle(
  threadId: string,
  title: string,
): Promise<string> {
  const { data } = await apiClient.patch<{ title: string }>(
    `/copilot/threads/${threadId}`,
    { title },
  );
  return data.title;
}

export async function generateCopilotThreadTitle(
  threadId: string,
): Promise<string> {
  const { data } = await apiClient.post<{ title: string }>(
    `/copilot/threads/${threadId}/generate-title`,
  );
  return data.title;
}
